import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { GetAllProdutoResponse } from '../../responses/produto/get-all-produto.response';
import { ReturnStruct } from '../../structs/return.struct';
import { ProdutoListaRequest } from '../../requests/produto/produto-lista.request';
import { ProdutoResponse } from '../../responses/produto/produto.response';
import { GetVariacoesGeradasResponse } from '../../responses/produto/get-variacoes-geradas.response';
import { CombinacaoVariacaoRequest } from '../../requests/produto/combinacao-variacao.request';
import { ProdutoRequest } from '../../requests/produto/produto.request';
import { LookupOnlineSelectResponse } from '../../responses/pessoa/lookup-online-select.response';
import { GetArquivoProdutoResponse } from '../../responses/produto/get-arquivo-produto.response';
import { CalcularPrecoAutomaticoRequest } from '../../requests/produto/calcular-preco-automatico.request';
import { CalcularPrecoAutomaticoResponse } from '../../responses/produto/configuracao-produto.response copy';
import { GetImpressaoProdutoSimplesResponse } from '../../responses/produto/get-impressao-produto-simples.response';
import { AlterarTagsProdutoRequest } from '../../requests/produto/alterar-tags-produto.request';
import { ProdutoBulkEditRequest } from '../../requests/produto/produto-bulk-edit.request';
import { ProdutoEtiquetaOpcoesCodigoCompostoRequest, ProdutoGerarDadosEtiquetaRequest } from '../../requests/produto/produto-gerar-dados-etiqueta.request';
import { ProdutoGerarDadosEtiquetaResponse } from '../../responses/produto/produto-gerar-dados-etiqueta.response';
import { ProdutoEtiquetaOpcoesCodigoCompostoResponse } from '../../responses/produto/produto-etiqueta-opcoes-codigo-composto.response';
import { ContarUsoAnpResponse } from '../../responses/produto/contar-uso-anp.response';

@Injectable({
  providedIn: 'root',
})
export class ProdutoService extends CommonService {
  // Ajustado para a rota [controller] -> Produto
  private readonly apiUrl: string = environment.urlApiProduto + 'Produto';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Obtém a lista de produtos paginada.
   * Corresponde a: [HttpPost("getAll")]
   * @param request Filtros da pesquisa
   * @param paginaAtual Índice da página
   */
  public getAll(request: ProdutoListaRequest, paginaAtual: number = 0): Observable<GetAllProdutoResponse> {
    const url = `${this.apiUrl}/getAll`;

    // Adicionando o parâmetro de query 'paginaAtual'
    const params = new HttpParams().set('paginaAtual', paginaAtual.toString());

    return this.httpClient
      .post<GetAllProdutoResponse>(url, request, {
        params,
        ...this.addHeaderToken()
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém os detalhes completos de um produto pelo ID.
   * Corresponde a: [HttpGet("idProduto/{idProduto}")]
   */
  public getById(idProduto: number): Observable<ProdutoResponse> {
    const url = `${this.apiUrl}/idProduto/${idProduto}`;
    return this.httpClient
      .get<ProdutoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Gera a grade de variações (produto cartesiano) com base nos atributos selecionados.
   * Corresponde a: [HttpPost("gerarGrade")]
   */
  public gerarGrade(request: CombinacaoVariacaoRequest): Observable<GetVariacoesGeradasResponse> {
    const url = `${this.apiUrl}/gerarGrade`;
    return this.httpClient
      .post<GetVariacoesGeradasResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
  * Gera a grade de variações (produto cartesiano) com base nos atributos selecionados.
  * Corresponde a: [HttpPost("gerarGrade")]
  */
  public calcularPreco(request: CalcularPrecoAutomaticoRequest): Observable<CalcularPrecoAutomaticoResponse> {
    const url = `${this.apiUrl}/CalcularPreco`;
    return this.httpClient
      .post<CalcularPrecoAutomaticoResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cadastra um novo produto (Simples, Kit, Fabricado ou Com Variação).
   * Corresponde a: [HttpPost]
   */
  public post(request: ProdutoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Atualiza um produto existente.
   * Corresponde a: [HttpPut("idProduto/{idProduto}")]
   */
  public put(idProduto: number, request: ProdutoRequest): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idProduto/${idProduto}`;
    return this.httpClient
      .put<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém o arquivo de um produto.
   * Corresponde a: [HttpGet("getArquivoProduto?urlCaminho={urlCaminho}")]
   * @param urlCaminho O caminho do arquivo.
   */
  public getArquivo(urlCaminho: string): Observable<GetArquivoProdutoResponse> {
    const url = `${this.apiUrl}/getArquivoProduto?urlCaminho=${urlCaminho}`;

    return this.httpClient
      .get<GetArquivoProdutoResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém a lista de produtos para impressão.
   * Corresponde a: [HttpPost("print")]
   */
  public getPrint(request: ProdutoListaRequest): Observable<GetImpressaoProdutoSimplesResponse> {
    const url = `${environment.urlApiProduto}produto/print`;
    return this.httpClient
      .post<GetImpressaoProdutoSimplesResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public gerarDadosEtiqueta(request: ProdutoGerarDadosEtiquetaRequest): Observable<ProdutoGerarDadosEtiquetaResponse> {
    const url = `${environment.urlApiProduto}EtiquetaProduto/gerar-dados-etiqueta`;
    return this.httpClient
      .post<ProdutoGerarDadosEtiquetaResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getOpcoesCodigoCompostoEtiqueta(request: ProdutoEtiquetaOpcoesCodigoCompostoRequest): Observable<ProdutoEtiquetaOpcoesCodigoCompostoResponse> {
    const url = `${environment.urlApiProduto}EtiquetaProduto/opcoes-codigo-composto`;
    return this.httpClient
      .post<ProdutoEtiquetaOpcoesCodigoCompostoResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Altera tags em massa para uma lista de produtos.
   * Corresponde a: [HttpPost("alterarTags")]
   * @param request Dados contendo IDs dos produtos, IDs das tags e ação (incluir/excluir)
   */
  public alterarTagsMassivo(request: AlterarTagsProdutoRequest): Observable<ReturnStruct> {
    const url = `${environment.urlApiProduto}produto/tags/bulk`;
    return this.httpClient
      .post<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Altera informações em massa para uma lista de produtos.
   * Corresponde a: [HttpPatch("bulk")]
   * @param request Dados parciais dos produtos a serem alterados em massa
   */
  public editarEmMassa(request: ProdutoBulkEditRequest): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/bulk`;
    return this.httpClient
      .patch<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Conta quantos produtos da empresa estão vinculados a um código ANP.
   * Usado pela tela Tabela ANP antes de excluir um código (aviso de uso).
   * Corresponde a: [HttpGet("anp/uso/{idAnpEmpresa}/contar")]
   */
  public contarUsoAnp(idAnpEmpresa: number): Observable<ContarUsoAnpResponse> {
    const url = `${this.apiUrl}/anp/uso/${idAnpEmpresa}/contar`;
    return this.httpClient
      .get<ContarUsoAnpResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
